'use client';
import { useEffect, useRef, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LaunchIcon from '@mui/icons-material/Launch';
import {
  Avatar,
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';

import { useT } from '@/hooks';
import { useActivityInfinite } from '@/services/query/marketdetails.queries';
import { EventResponse, MarketActivity } from '@/types';
import { formatNumber, formatRelativeTime } from '@/utils';

import { EXTERNAL_LINKS } from '@/constants';

interface ActivityTabProps {
  event: EventResponse;
}

const FilterSelect = ({
  value,
  options,
  onChange,
}: {
  value: string;
  options: { label: string; value: string }[];
  onChange: (e: SelectChangeEvent) => void;
}) => {
  const renderOption = (opt: { label: string; value: string }) => (
    <MenuItem key={opt.value} value={opt.value}>
      {opt.label}
    </MenuItem>
  );

  const menuItems = options.map(renderOption);

  return (
    <FormControl size="small">
      <Select
        value={value}
        onChange={onChange}
        IconComponent={ExpandMoreIcon}
        sx={{
          borderRadius: 10,
          bgcolor: 'background.subtle',
          '& .MuiSelect-select': {
            py: 0.75,
            px: 2,
            pr: '32px !important',
            fontSize: '0.875rem',
            fontWeight: 600,
            color: 'text.primary',
          },
          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
          '&:hover': {
            bgcolor: 'background.hover',
          },
        }}
      >
        {menuItems}
      </Select>
    </FormControl>
  );
};

const ExternalLink = ({ txHash }: { txHash: string }) => (
  <IconButton
    size="small"
    sx={{ color: 'text.secondary', p: 0.5 }}
    component="a"
    href={EXTERNAL_LINKS.POLYGONSCAN_TX(txHash)}
    target="_blank"
  >
    <LaunchIcon sx={{ fontSize: 16 }} />
  </IconButton>
);

const ActivityRow = ({ activity }: { activity: MarketActivity }) => {
  const { t } = useT();
  const isNo = activity.outcome.toLowerCase() === 'no';
  const color = isNo ? 'error.main' : 'success.main';
  const timestampMs = activity.timestamp * 1000;
  const timeAgo = formatRelativeTime(new Date(timestampMs));
  const formattedAmount = formatNumber(activity.size);
  const displayName = activity.name || activity.pseudonym || 'Anonymous';
  const initials = displayName[0]?.toUpperCase() || '?';

  // Calculate USD value (size * price)
  const usdValue = activity.size * activity.price;
  const formattedPrice = (activity.price * 100).toFixed(1);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
        <Avatar
          src={activity.profileImage}
          sx={{
            width: 36,
            height: 36,
            fontSize: '0.875rem',
            fontWeight: 600,
            background: (theme) =>
              `linear-gradient(45deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
            color: 'white',
          }}
        >
          {!activity.profileImage && initials}
        </Avatar>

        <Box
          sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, minWidth: 0 }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              maxWidth: { xs: 120, sm: 180, md: 250 },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {displayName}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {activity.side.toLowerCase() === 'buy'
              ? t('common.buy', { defaultValue: 'buy' }).toLowerCase()
              : t('common.sell', { defaultValue: 'sell' }).toLowerCase()}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color }}>
            {formattedAmount} {activity.outcome}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('market.for', { defaultValue: 'for' })}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {activity.title}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('market.at', { defaultValue: 'at' })} {formattedPrice}¢
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', ml: 0.5 }}>
            (${usdValue.toFixed(0)})
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2, flexShrink: 0 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
          {timeAgo}
        </Typography>
        <ExternalLink txHash={activity.transactionHash} />
      </Box>
    </Box>
  );
};

const ActivitiesList = ({
  activities,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
}: {
  activities: MarketActivity[];
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
}) => {
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }

    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const renderActivity = (activity: MarketActivity) => (
    <ActivityRow key={activity.transactionHash} activity={activity} />
  );

  const activitiesItems = activities.map(renderActivity);

  return (
    <Box>
      {activitiesItems}

      {/* Scroll Sentinel */}
      <Box
        ref={sentinelRef}
        sx={{
          height: 40,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mt: 2,
        }}
      >
        {isFetchingNextPage && <CircularProgress size={24} />}
      </Box>
    </Box>
  );
};

export const ActivityTab = ({ event }: ActivityTabProps) => {
  const { t } = useT();
  const [side, setSide] = useState<'all' | 'BUY' | 'SELL'>('all');
  const [minAmount, setMinAmount] = useState('0');

  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useActivityInfinite({
      eventId: event.id,
      limit: 10,
      takerOnly: true,
      filterType: 'CASH',
      filterAmount: parseInt(minAmount),
      side: side === 'all' ? undefined : side,
    });

  const handleSideChange = (e: SelectChangeEvent) => setSide(e.target.value as any);
  const handleMinAmountChange = (e: SelectChangeEvent) => setMinAmount(e.target.value);

  const sideOptions = [
    { label: t('market.all', { defaultValue: 'All' }), value: 'all' },
    { label: t('market.buys', { defaultValue: 'Buys' }), value: 'BUY' },
    { label: t('market.sells', { defaultValue: 'Sells' }), value: 'SELL' },
  ];

  const minAmountOptions = [
    { label: t('market.minAmountFilter', { defaultValue: 'Min amount' }), value: '0' },
    { label: '>$10', value: '10' },
    { label: '>$100', value: '100' },
    { label: '>$1000', value: '1000' },
  ];

  const activities = data?.pages.flatMap((page) => page.data) || [];

  let listContent;
  if (isLoading) {
    listContent = (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={40} />
      </Box>
    );
  } else if (isError) {
    listContent = (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="error">{t('market.failedToLoadActivity')}</Typography>
      </Box>
    );
  } else if (activities.length === 0) {
    listContent = (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <Typography color="text.secondary">{t('market.noActivityFound')}</Typography>
      </Box>
    );
  } else {
    listContent = (
      <ActivitiesList
        activities={activities}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    );
  }

  return (
    <Box sx={{ maxWidth: { xs: 330, md: '100%' } }}>
      {/* Filters Header */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 3 }}>
        <FilterSelect value={side} options={sideOptions} onChange={handleSideChange} />
        <FilterSelect
          value={minAmount}
          options={minAmountOptions}
          onChange={handleMinAmountChange}
        />
      </Stack>

      {/* List Content */}
      <Box sx={{ minHeight: 400 }}>{listContent}</Box>
    </Box>
  );
};
